import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Shield, Phone, ShieldCheck } from 'lucide-react';
import useSEO from '../hooks/useSEO';
import leakPhoto from '../assets/dde.webp';
import heroPhoto from '../assets/technicien_camera_thermique.webp';
import { pageByPath } from '../content';

const leakTypes = [
  '/recherche-fuite-canalisation',
  '/recherche-fuite-chauffage',
  '/recherche-fuite-evacuation',
  '/recherche-fuite-douche',
  '/recherche-infiltration',
  '/surconsommation-eau'
];

const belgianCities = [
  '/recherche-fuite-arlon',
  '/recherche-fuite-aubange',
  '/recherche-fuite-messancy',
  '/recherche-fuite-virton'
];

const processSteps = [
  {
    number: '01',
    title: 'Contact',
    description: 'Vous nous contactez par téléphone ou via le formulaire, sans engagement.'
  },
  {
    number: '02',
    title: 'Intervention',
    description: 'Nos experts se déplacent et réalisent un diagnostic précis sur place.'
  },
  {
    number: '03',
    title: 'Rapport',
    description: 'Vous recevez un rapport complet et détaillé sous 72h.'
  },
  {
    number: '04',
    title: 'Suivi',
    description: 'Nous vous accompagnons jusqu’à la résolution de votre dossier.'
  }
];

const stats = [
  {
    number: '20+',
    label: "Années d'expérience",
    description: 'Une expertise reconnue en assurance et recherche de fuites'
  },
  {
    number: '1000+',
    label: 'Sinistres résolus',
    description: 'Des solutions rapides et efficaces pour nos clients'
  },
  {
    number: '24/7',
    label: 'Équipe dédiée',
    description: 'Des experts qualifiés et réactifs'
  },
  {
    number: '100%',
    label: 'Technologies modernes',
    description: 'Des diagnostics précis et non destructifs'
  }
];

const Home = () => {
  useSEO({
    title: "Expomis – Recherche de fuite & Expertise Assurance au Luxembourg",
    description: "Recherche de fuite non destructive et expertise d'assurance au Luxembourg et dans le sud de la province de Luxembourg (Arlon, Virton). Rapport sous 72h.",
    path: '/'
  });

  return (
    <div>
      {/* Hero */}
      <section className="pt-40 pb-20 md:pt-48 md:pb-24">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
            <div className="flex flex-col gap-7">
              <div className="text-xs font-semibold tracking-widest uppercase text-brand">
                L'expertise est notre métier
              </div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-ink leading-[1.08]">
                Recherche de fuite et expertise d'assurance au Luxembourg
              </h1>
              <p className="text-lg text-ink-soft leading-relaxed max-w-lg">
                Expomis localise les fuites d'eau et les infiltrations sans démolition, et expertise les sinistres
                pour les compagnies d'assurance comme pour les assurés. Nous intervenons dans tout le Grand-Duché
                et dans le sud de la province de Luxembourg, en Belgique, depuis Steinfort.
              </p>
              <div className="flex flex-wrap items-center gap-8 mt-2">
                <Link
                  to="/recherche-fuite-luxembourg"
                  className="px-8 py-4 bg-ink text-white text-sm font-medium hover:bg-brand-deep transition-colors"
                >
                  Recherche de fuite
                </Link>
                <Link
                  to="/contact"
                  className="flex items-center gap-2 text-sm font-medium text-ink hover:text-brand transition-colors"
                >
                  Nous contacter
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="relative aspect-square bg-gradient-to-br from-line/60 to-line/20 rounded-sm overflow-hidden">
              <img
                src={heroPhoto}
                alt="Technicien contrôlant un mur à la caméra thermique"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white px-6 py-5 border-l-2 border-brand">
                <div className="flex items-center gap-2 text-ink font-serif text-lg font-semibold mb-1">
                  <Phone className="w-4 h-4 text-brand" />
                  +352 661 181 082
                </div>
                <p className="text-sm text-ink-soft">Intervention d'urgence disponible 24h/24</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contre-expertise */}
      <section className="bg-ink text-white">
        <div className="container mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row items-start md:items-center gap-6">
          <ShieldCheck className="w-8 h-8 text-brand-cyan flex-shrink-0" strokeWidth={1.4} />
          <div className="flex-1">
            <p className="text-lg leading-relaxed">
              <span className="font-serif font-semibold">Besoin d'un contre-expert ?</span> Nous pouvons intervenir de façon indépendante, à vos côtés face à l'expert mandaté par votre assureur.
              Faire appel à un contre-expert est un droit prévu par votre contrat, pas un signe de conflit,
              et les frais sont souvent couverts, en tout ou partie, par votre contrat d'assurance.
            </p>
          </div>
          <Link
            to="/contre-expertise"
            className="flex-shrink-0 flex items-center gap-2 text-sm font-medium text-brand-cyan hover:text-white transition-colors whitespace-nowrap"
          >
            La contre-expertise
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Stats */}
      <section className="container mx-auto px-6 md:px-12 pb-24">
        <div className="grid grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => {
            const borderClasses = [
              'border-t border-line pt-8 pr-8',
              'border-t border-l border-line pt-8 pl-8 lg:pr-8',
              'border-t border-line pt-8 pr-8 lg:border-l lg:pl-8',
              'border-t border-l border-line pt-8 pl-8'
            ][index];
            return (
              <div key={index} className={borderClasses}>
                <div className="font-serif text-4xl md:text-5xl font-semibold text-brand">{stat.number}</div>
                <div className="text-sm font-semibold text-ink mt-3">{stat.label}</div>
                <p className="text-sm text-ink-faint mt-2 leading-relaxed">{stat.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Services teaser */}
      <section className="bg-white border-t border-line">
        <div className="container mx-auto px-6 md:px-12 py-24">
          <div className="flex flex-col gap-3 mb-14">
            <div className="text-xs font-semibold tracking-widest uppercase text-brand">
              Nos domaines d'intervention
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ink">
              Deux expertises, un même engagement
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <img
                src={leakPhoto}
                alt="Détection de fuite d'eau sur une canalisation en cuivre"
                className="w-full h-56 object-cover rounded-sm"
              />
              <div className="border-t-2 border-brand mt-6 pt-5">
                <div className="flex items-center gap-2 mb-3">
                  <Search className="w-5 h-5 text-brand" />
                  <h3 className="font-serif text-2xl font-semibold text-ink">Détection de fuites</h3>
                </div>
                <p className="text-ink-soft leading-relaxed mb-4">
                  Solutions rapides, efficaces et non destructives pour localiser précisément vos fuites d'eau.
                </p>
                <Link
                  to="/recherche-fuite-luxembourg"
                  className="flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-deep transition-colors"
                >
                  La recherche de fuite
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div>
              <div className="w-full h-56 rounded-sm bg-[repeating-linear-gradient(135deg,#e7ecf2_0,#e7ecf2_1px,transparent_1px,transparent_14px)] flex items-center justify-center">
                <Shield className="w-14 h-14 text-brand" strokeWidth={1.3} />
              </div>
              <div className="border-t-2 border-brand mt-6 pt-5">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-5 h-5 text-brand" />
                  <h3 className="font-serif text-2xl font-semibold text-ink">Expertise assurance</h3>
                </div>
                <p className="text-ink-soft leading-relaxed mb-4">
                  Expertise assurance et contre-expertise indépendante : incendie, vol, tempête, dégâts des eaux et responsabilité civile.
                </p>
                <Link
                  to="/expertise-assurance"
                  className="flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-deep transition-colors"
                >
                  L'expertise assurance
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types de fuite */}
      <section className="container mx-auto px-6 md:px-12 pt-24">
        <div className="flex flex-col gap-3 mb-14">
          <div className="text-xs font-semibold tracking-widest uppercase text-brand">
            Recherche de fuite
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ink">
            Quel type de fuite ?
          </h2>
          <p className="text-ink-soft leading-relaxed max-w-2xl">
            {'Chaque fuite appelle sa méthode. Nous disposons de '}
            <Link to="/techniques" className="text-brand underline underline-offset-2">neuf techniques non destructives</Link>
            {" : caméra thermique, écoute acoustique, gaz traceur, fluorescéine, test fumigène, caméra d'inspection…"}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
          {leakTypes.map(path => (
            <Link key={path} to={path} className="group border-t-2 border-brand pt-5 block">
              <h3 className="font-serif text-xl font-semibold text-ink mb-2 group-hover:text-brand transition-colors">
                {pageByPath[path].nav}
              </h3>
              <p className="text-sm text-ink-soft leading-relaxed">{pageByPath[path].summary}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Zones */}
      <section className="container mx-auto px-6 md:px-12 pt-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="border-t-2 border-brand pt-5">
            <div className="text-xs font-semibold tracking-widest uppercase text-brand mb-3">Grand-Duché</div>
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-ink mb-4">
              <Link to="/recherche-fuite-luxembourg" className="hover:text-brand">Recherche de fuite au Luxembourg</Link>
            </h2>
            <p className="text-ink-soft leading-relaxed">
              <Link to="/recherche-fuite-luxembourg-ville" className="text-brand underline underline-offset-2">Luxembourg-Ville</Link>, <Link to="/recherche-fuite-esch-sur-alzette" className="text-brand underline underline-offset-2">Esch-sur-Alzette</Link>, <Link to="/recherche-fuite-differdange" className="text-brand underline underline-offset-2">Differdange</Link>, <Link to="/recherche-fuite-dudelange" className="text-brand underline underline-offset-2">Dudelange</Link>, <Link to="/recherche-fuite-petange" className="text-brand underline underline-offset-2">Pétange</Link>, <Link to="/recherche-fuite-mamer" className="text-brand underline underline-offset-2">Mamer</Link>, <Link to="/recherche-fuite-strassen" className="text-brand underline underline-offset-2">Strassen</Link>, <Link to="/recherche-fuite-bertrange" className="text-brand underline underline-offset-2">Bertrange</Link>, <Link to="/recherche-fuite-hesperange" className="text-brand underline underline-offset-2">Hesperange</Link>, <Link to="/recherche-fuite-sanem" className="text-brand underline underline-offset-2">Sanem</Link>, <Link to="/recherche-fuite-mersch" className="text-brand underline underline-offset-2">Mersch</Link>, <Link to="/recherche-fuite-ettelbruck" className="text-brand underline underline-offset-2">Ettelbruck</Link>, <Link to="/recherche-fuite-diekirch" className="text-brand underline underline-offset-2">Diekirch</Link>, <Link to="/recherche-fuite-wiltz" className="text-brand underline underline-offset-2">Wiltz</Link>, <Link to="/recherche-fuite-grevenmacher" className="text-brand underline underline-offset-2">Grevenmacher</Link>, <Link to="/recherche-fuite-remich" className="text-brand underline underline-offset-2">Remich</Link>, <Link to="/recherche-fuite-echternach" className="text-brand underline underline-offset-2">Echternach</Link>, <Link to="/recherche-fuite-steinfort" className="text-brand underline underline-offset-2">Steinfort</Link>
              et toutes les communes du pays.
            </p>
          </div>
          <div className="border-t-2 border-brand pt-5">
            <div className="text-xs font-semibold tracking-widest uppercase text-brand mb-3">Belgique</div>
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-ink mb-4">
              <Link to="/recherche-fuite-province-luxembourg" className="hover:text-brand">Sud de la province de Luxembourg</Link>
            </h2>
            <p className="text-ink-soft leading-relaxed">
              {belgianCities.map((path, i) => (
                <React.Fragment key={path}>
                  {i > 0 && ', '}
                  <Link to={path} className="text-brand underline underline-offset-2">
                    {pageByPath[path].nav.replace('Recherche de fuite à ', '')}
                  </Link>
                </React.Fragment>
              ))}
              , Attert, Habay, Étalle, Musson, Saint-Léger et les communes voisines.
            </p>
          </div>
        </div>
      </section>

      {/* Comment ça se passe */}
      <section className="container mx-auto px-6 md:px-12 py-24">
        <div className="flex flex-col gap-3 mb-14">
          <div className="text-xs font-semibold tracking-widest uppercase text-brand">
            Notre méthode
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ink">
            Comment ça se passe
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {processSteps.map((step, index) => (
            <div key={index} className="border-t-2 border-brand pt-5">
              <div className="font-serif text-3xl font-semibold text-ink-faint mb-3">{step.number}</div>
              <h3 className="font-semibold text-ink mb-2">{step.title}</h3>
              <p className="text-sm text-ink-soft leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="bg-paper border-t border-line">
        <div className="container mx-auto px-6 md:px-12 py-20 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ink mb-4">
            Un sinistre en cours ? Une question ?
          </h2>
          <p className="text-lg text-ink-soft leading-relaxed max-w-xl mx-auto mb-8">
            Contactez-nous sans engagement — nous vous répondons sous 2h en moyenne.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-ink text-white text-sm font-medium hover:bg-brand-deep transition-colors"
          >
            Nous contacter
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
